import { useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Copy, Download, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
const HEADERS = ["Booking ID", "Guest name", "Old total", "New total", "Old status", "New status",
  "Quote amount", "Quote gst_required", "Booking gst_included", "Adults (booking)", "Adults (quote)", "GST note"];
const yn = (v) => (v === null || v === undefined ? "" : v ? "yes" : "no");

// TEMPORARY, READ-ONLY: only ever calls the backfill endpoint with dry_run=true.
const AdminResyncPreview = () => {
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/backfill-gst-total?dry_run=true`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load preview");
    } finally {
      setLoading(false);
    }
  };

  const rows = (data?.preview || []).map((p) => [
    p.booking_id, p.guest_name || "", p.before.total_amount, p.after.total_amount,
    p.before.payment_status || "", p.after.payment_status || "",
    p.quote_amount ?? "", yn(p.quote_gst_required), yn(p.booking_gst_included),
    p.booking_adults ?? "", p.quote_adults ?? "", p.gst_note || "",
  ]);

  const toTsv = () => [HEADERS, ...rows].map((r) => r.join("\t")).join("\n");
  const copy = async () => {
    try { await navigator.clipboard.writeText(toTsv()); toast.success("Table copied"); }
    catch { toast.error("Copy failed"); }
  };
  const download = () => {
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [HEADERS, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "booking-resync-preview.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Strictly admin: the route guard also lets employees in, so enforce it here too.
  if (user && user.role !== "admin") return <Navigate to="/admin" replace />;

  return (
    <DashboardLayout type="admin">
      <div className="space-y-6" data-testid="admin-resync-preview">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Preview booking resync</h1>
          <p className="text-gray-500">Read-only dry run. Nothing is saved and no payments are touched.</p>
        </div>
        <button onClick={run} disabled={loading} data-testid="run-preview"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
          {data ? "Refresh preview" : "Run preview"}
        </button>

        {data && (
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader className="pb-2 border-b border-gray-100">
              <CardTitle className="text-gray-900 text-lg">
                {data.updated} booking(s) would change · {data.unchanged} unchanged
              </CardTitle>
              <p className="text-sm text-gray-600" data-testid="pending-counts">
                Pending payments: <b>{data.pending_payments_before}</b> before → <b>{data.pending_payments_after}</b> after
              </p>
            </CardHeader>
            <CardContent className="pt-4 space-y-3">
              <div className="flex gap-2">
                <button onClick={copy} className="inline-flex items-center gap-1 px-3 py-1.5 border rounded-md text-sm"><Copy className="w-4 h-4" />Copy table</button>
                <button onClick={download} className="inline-flex items-center gap-1 px-3 py-1.5 border rounded-md text-sm"><Download className="w-4 h-4" />Download CSV</button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="preview-table">
                  <thead><tr className="text-left text-gray-500 border-b">
                    {HEADERS.map((h) => <th key={h} className="py-2 pr-4 font-medium">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r[0]} className="border-b border-gray-100">
                        <td className="py-2 pr-4 font-mono">{r[0].slice(0, 8)}</td>
                        <td className="py-2 pr-4">{r[1]}</td>
                        <td className="py-2 pr-4">₹{fmt(r[2])}</td>
                        <td className="py-2 pr-4 font-medium">₹{fmt(r[3])}</td>
                        <td className="py-2 pr-4">{r[4]}</td>
                        <td className="py-2 pr-4 font-medium">{r[5]}</td>
                        <td className="py-2 pr-4">{r[6] === "" ? "-" : `₹${fmt(r[6])}`}</td>
                        <td className="py-2 pr-4">{r[7] || "-"}</td>
                        <td className="py-2 pr-4">{r[8]}</td>
                        <td className="py-2 pr-4">{r[9] === "" ? "-" : r[9]}</td>
                        <td className="py-2 pr-4">{r[10] === "" ? "-" : r[10]}</td>
                        <td className="py-2 pr-4 text-xs text-gray-600">{r[11]}</td>
                      </tr>
                    ))}
                    {rows.length === 0 && <tr><td colSpan={12} className="py-4 text-gray-500">No bookings would change.</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminResyncPreview;
